import { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { normRole, normInstructorStatus, userNeedsPhone, isShortCourseLearner, isApprovedShortCourseFacilitator } from '../utils/userProfile';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    if (authService.isAuthenticated()) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Auth check failed:', error);
        authService.logout();
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    try {
      const profile = await authService.getCurrentUser();
      setUser(profile);
    } catch {
      setUser(data.user);
    }
    return data;
  };

  const refreshUser = async () => {
    if (authService.isAuthenticated()) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const addPhone = async (phone) => {
    const data = await authService.addPhone(phone);
    if (data.user) {
      setUser(data.user);
    } else {
      await refreshUser();
    }
    return data;
  };

  const register = async (userData, options = {}) => {
    const data = await authService.register(userData);
    if (!options.deferUser) {
      try {
        const profile = await authService.getCurrentUser();
        setUser(profile);
      } catch {
        setUser(data.user);
      }
    }
    return data;
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      // Clear any cached data
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('token');
      }
    }
  };

  // Make refreshUser available globally for content viewing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.refreshUser = refreshUser;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.refreshUser;
      }
    };
  }, [refreshUser]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    addPhone,
    requiresPhone: userNeedsPhone(user),
    isAuthenticated: !!user,
    isAdmin: normRole(user) === 'admin',
    isInstructor: normRole(user) === 'instructor',
    isStudent: normRole(user) === 'student' || normRole(user) === 'user',
    isShortCourseLearner: isShortCourseLearner(user),
    isApprovedInstructor: normRole(user) === 'instructor' && normInstructorStatus(user) === 'approved',
    isApprovedShortCourseFacilitator: isApprovedShortCourseFacilitator(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
